import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { TimePicker } from '@mui/x-date-pickers';
import { TIME_24_HOUR } from '@application/utilities/date-formats';
import { ErrorWithIcon } from '@ui/components/common/TextField/ErrorWithIcon';
import { Box, buttonClasses, useTheme } from '@mui/material';
import { REGISTRATION_FIELD_FILL_BG } from '../registrationFormFieldStyles';
import dayjs from 'dayjs';
import InfoIconTooltip from '@ui/components/common/Tooltips/InfoIconTooltip';
const ETimePicker = ({ name, control, label, rules, error, bgColor = REGISTRATION_FIELD_FILL_BG, selectedTone = 'darker', // 👈 default to current behavior
disabled, tooltipText, ...rest }) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const pickerProps = {
        views: ['hours', 'minutes'],
        openTo: 'hours'
    };
    // choose between primary.main and primary.darker (fallback to .dark if .darker missing)
    const selectedBg = selectedTone === 'main'
        ? theme.palette.primary.main
        : (theme.palette.primary.darker ?? theme.palette.primary.dark);
    const styledLabel = useMemo(() => !label ? undefined : (_jsxs(Box, { display: 'inline-flex', alignItems: 'center', gap: '4px', sx: { pointerEvents: 'auto' }, children: [label, tooltipText && (_jsx(Box, { sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                }, children: _jsx(InfoIconTooltip, { title: tooltipText, size: 'small', variant: 'dark' }) }))] })), [label, tooltipText]);
    return (_jsxs(Box, { width: '100%', children: [_jsx(Controller, { name: name, control: control, rules: rules, render: ({ field }) => {
                    let safeValue = null;
                    if (field.value instanceof Date)
                        safeValue = dayjs(field.value);
                    else if (field.value && typeof field.value === 'string')
                        safeValue = dayjs(field.value);
                    return (_jsx(TimePicker, { open: open, disabled: disabled, ampm: false, format: TIME_24_HOUR, onOpen: () => setOpen(true), onClose: () => setOpen(false), label: styledLabel, value: safeValue && safeValue.isValid && safeValue.isValid() ? safeValue : null, onChange: (date) => {
                            if (date && date.isValid()) {
                                field.onChange(date.toDate());
                            }
                            else {
                                field.onChange(null);
                            }
                        }, ...pickerProps, slotProps: {
                            textField: {
                                onClick: () => {
                                    if (!disabled)
                                        setOpen(true);
                                },
                                fullWidth: true,
                                variant: 'filled',
                                sx: {
                                    '.MuiInputBase-root.MuiFilledInput-root': {
                                        backgroundColor: `${bgColor} !important`,
                                        borderRadius: '12px !important',
                                        overflow: 'hidden !important',
                                        pr: 2,
                                        cursor: !disabled ? 'pointer' : 'unset'
                                    },
                                    '.MuiIconButton-root': {
                                        border: 'none !important',
                                        color: '#454F5B !important',
                                        boxShadow: 'none !important'
                                    }
                                }
                            },
                            // selected digital clock item color
                            popper: {
                                sx: {
                                    '& .MuiPickersLayout-actionBar': {
                                        [`& .${buttonClasses.root}:last-of-type`]: {
                                            backgroundColor: selectedBg,
                                            color: theme.palette.mode === 'light'
                                                ? theme.palette.common.white
                                                : theme.palette.grey[800]
                                        }
                                    },
                                    '& .MuiMenuItem-root.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                                        backgroundColor: selectedBg,
                                        color: theme.palette.primary.contrastText,
                                        '&:hover': { backgroundColor: selectedBg }
                                    }
                                }
                            },
                            mobilePaper: {
                                sx: {
                                    '& .MuiMenuItem-root.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                                        backgroundColor: selectedBg,
                                        color: theme.palette.primary.contrastText,
                                        '&:hover': { backgroundColor: selectedBg }
                                    }
                                }
                            }
                        }, ...rest }));
                } }), _jsx(ErrorWithIcon, { error: error, mt: 0.5, ml: 0.5 })] }));
};
export default ETimePicker;
